<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <AppFormWrapper id="sign-in-verify-form" @submit.prevent="onSubmit">
    <!-- the wait state while the code is checked and the session starts (2026-10-15) -->
    <AppModalDialogLoading :is-busy="loading" busy-message="Signing in…" />
    <h2>Verify Your Email</h2>
    <p>Enter the 6-digit code sent to your address.</p>
    <CodeBox />
    <small>Didn't receive the email? Check your spam folder or start again to request a new code.</small>
    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
  </AppFormWrapper>
</template>

<script setup>
import {computed, provide, ref, watch, watchEffect} from "vue";
import {useClerk, useSignIn, useSignUp} from "@clerk/vue";
import AppFormWrapper from "@/components/App/Form/Wrapper";
import CodeBox from "@/components/App/Modal/SignIn/CodeBox";
import AppModalDialogLoading from "@/components/App/Modal/Dialog/Loading";
import {fetchIsSessionActivated, getCodeErrorMessage} from "@/composables/useSignInRecovery";
import {SUBSCRIPTION_LISTS} from "@/composables/subscriptionLists";
import {useSaveIsSubscribed} from "@/composables/useSaveIsSubscribed";

const props = defineProps(["email", "isExistingUser"]);
const emit = defineEmits(["success", "needsName", "update:canSubmit"]);

const {signIn} = useSignIn();
const {signUp} = useSignUp();
const clerk = useClerk();
const {save: saveSettings} = useSaveIsSubscribed();

const verificationCode = ref("");
const loading = ref(false);
const errorMessage = ref("");
const isReady = computed(() => verificationCode.value.length === 6);
const canSubmit = computed(() => isReady.value && !loading.value);
watch(canSubmit, (val) => emit("update:canSubmit", val), {immediate: true});

provide("verificationCode", verificationCode);

async function attemptVerification() {
  const code = verificationCode.value;
  if (props.isExistingUser) {
    return signIn.value.attemptFirstFactor({strategy: "email_code", code});
  }
  return signUp.value.attemptEmailAddressVerification({code});
}

// accepted: the code stays in the boxes while the session starts (2026-10-15: they were
// emptied first, and a slow start left the dialog looking stuck)
// a new account: all three mailing lists on (Seán, 2026-11-02), shown at once in #profile; the
// fullstack's user.created webhook sets the same for an account made any other way
async function onComplete(result) {
  await fetchIsSessionActivated(clerk.value, result.createdSessionId);
  const subscriptions = SUBSCRIPTION_LISTS;
  if (!props.isExistingUser) await saveSettings(clerk.value.user, {subscriptions});
  emit("success");
}

// refused, but the browser may already hold the session (the code was accepted a moment ago):
// then on as signed in; else a plain message, and the boxes emptied for another try
async function onError(error) {
  if (await fetchIsSessionActivated(clerk.value)) return emit("success");
  errorMessage.value = getCodeErrorMessage(error);
  verificationCode.value = "";
}

async function onSubmit() {
  if (!isReady.value || loading.value) return;
  errorMessage.value = "";
  loading.value = true;
  try {
    const result = await attemptVerification();
    if (result.status === "complete") return await onComplete(result);
    if (result.status === "missing_requirements") return emit("needsName");
    errorMessage.value = `Verification failed (status: ${result.status})`;
  } catch (error) {
    await onError(error);
  } finally {
    loading.value = false;
  }
}

watchEffect(() => {
  if (isReady.value && !loading.value) onSubmit();
});
</script>

